/**
 * Investigation Pipeline — Kanban-style workflow view for AML/CTF investigations
 * Maps API case status to pipeline stages; links to InvestigationCase for detail.
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { buildInvestigationWorkspaceCasesQuery } from '../../config/investigationWorkspace'
import { CASE_RESOLUTION_OPTIONS } from './components/CaseCloseModal'
import {
  resolvePipelineStage,
  resolvePatronListTitle,
  resolvePatronListSecondaryId,
  dedupeInvestigationCasesForPipeline,
} from '../../utils/workspaceCaseStage'
import { useAuth } from '../../contexts/AuthContext'
import { useStaffMembers } from '../../hooks/useStaffMembers'
import {
  AlertTriangle, CheckCircle2, Clock, Shield, FileText,
  Eye, ChevronRight, ArrowRight, ArrowLeft,
  Inbox, Paperclip, X, ExternalLink, Lock,
  DollarSign, Search, Zap, CheckSquare,
  Settings, User, ChevronDown, Trash2, UserCheck, Type
} from 'lucide-react'

/* ─── CONSTANTS ─── */
const F = { sans: "'Poppins', system-ui, sans-serif", mono: "'DM Mono', monospace" }
const T = { small: 12, normal: 14 } // typography: smallest 12px, normal 14px
const C = {
  pri: '#0a5169', priD: '#07384a', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  orange: '#ea580c', orangeBg: '#fff7ed', orangeBd: '#fed7aa',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a', border: '#e2e8f0',
}

// API status → pipeline stage (1:1 mapping after migration 083 added missing statuses)
// Pipeline starts at Investigation — alerts live in Monitor Safe (/monitor-safe/alerts)
const STATUS_TO_STAGE = {
  OPEN: 'investigation',
  INVESTIGATING: 'investigation',
  PENDING_EVIDENCE: 'pending_evidence',
  SUSPICION_FORMED: 'suspicion_formed',
  SMR_DRAFT: 'smr_draft',
  ESCALATED: 'investigation',
  PENDING_REVIEW: 'smr_draft',
  PENDING_APPROVAL: 'under_review',
  FILED: 'filed',
  RESOLVED: 'closed',
  CLOSED: 'closed',
}

/** Map API case status → Kanban stage id for timeline. Unified — all cases are INVESTIGATION. */
function apiStatusToTimelineStage(status) {
  const s = String(status || 'INVESTIGATING').toUpperCase()
  return STATUS_TO_STAGE[s] || null
}

function parseActivityPayload(val) {
  if (val == null) return null
  if (typeof val === 'object') return val
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return null
    }
  }
  return null
}

/** Resolve performer from case_activities row (API may send snake_case or camelCase). */
function pickActorFromActivity(a) {
  if (!a || typeof a !== 'object') return 'Unknown user'
  const meta = a.metadata && typeof a.metadata === 'object' ? a.metadata : null
  const name = a.created_by_name ?? a.createdByName ?? meta?.performed_by_name ?? meta?.user_name
  const id = a.created_by ?? a.createdBy ?? meta?.performed_by ?? meta?.user_id
  const fromName = name != null && String(name).trim() !== '' ? String(name).trim() : ''
  const fromId = id != null && String(id).trim() !== '' ? String(id).trim() : ''
  return fromName || fromId || 'Unknown user'
}

const STAGE_TO_STATUS = {
  investigation: 'INVESTIGATING',
  pending_evidence: 'PENDING_EVIDENCE',
  suspicion_formed: 'SUSPICION_FORMED',
  smr_draft: 'SMR_DRAFT',
  under_review: 'PENDING_APPROVAL',
  filed: 'FILED',
  closed: 'CLOSED',
}

const STAGES = [
  { id: 'investigation', label: 'Investigation', sub: 'Gathering evidence', color: C.pri, bg: C.priM, icon: Search },
  { id: 'pending_evidence', label: 'Pending Evidence', sub: 'Waiting on external', color: C.amber, bg: C.amberBg, icon: Clock },
  { id: 'suspicion_formed', label: 'Suspicion Formed', sub: 'SMR clock running', color: C.red, bg: C.redBg, icon: AlertTriangle },
  { id: 'smr_draft', label: 'SMR Draft', sub: 'CO preparing filing', color: C.orange, bg: C.orangeBg, icon: FileText },
  { id: 'under_review', label: 'SMR Approved', sub: 'Manager approved, ready to file', color: C.purple, bg: C.purpleBg, icon: Eye },
  { id: 'filed', label: 'SMR Filed', sub: 'Submitted to AUSTRAC', color: C.green, bg: C.greenBg, icon: CheckCircle2 },
  { id: 'closed', label: 'Closed', sub: 'Internally closed (no SMR filed)', color: C.slateL, bg: C.slateBg, icon: CheckCircle2 },
]

/** Legacy pipeline used short ids (SC, JP, …) before staff.id was canonical. Remap saved routing. */
const LEGACY_ASSIGNEE_ID_TO_NAME = {
  SC: 'Sarah Chen',
  JP: 'James Park',
  MW: 'Marcus Webb',
  LT: 'Linda Tran',
  AB: 'Anna Burgess',
}

function remapRoutingAssignees(routing, members) {
  if (!routing || typeof routing !== 'object') return {}
  const byName = new Map(members.map((m) => [String(m.name || '').toLowerCase().trim(), m]))
  const resolveId = (rawId) => {
    if (rawId == null || rawId === '') return null
    const sid = String(rawId)
    if (members.some((m) => String(m.id) === sid)) return sid
    const legacyName = LEGACY_ASSIGNEE_ID_TO_NAME[sid]
    if (legacyName) {
      const m = byName.get(legacyName.toLowerCase())
      return m ? String(m.id) : null
    }
    return null
  }
  const out = {}
  for (const [k, v] of Object.entries(routing)) {
    if (!v || typeof v !== 'object') continue
    const da = resolveId(v.defaultAssignee)
    const obs = Array.isArray(v.observers)
      ? [...new Set(v.observers.map(resolveId).filter(Boolean))]
      : []
    out[k] = { ...v, defaultAssignee: da, observers: obs }
  }
  return out
}

// Build role-based routing defaults from team member list
function buildRoutingDefaults(members) {
  const firstCo = members.find(m => m.role === 'co')?.id || null
  const firstMgr = members.find(m => m.role === 'manager')?.id || null
  const mgrIds = members.filter(m => m.role === 'manager').map(m => m.id)
  return {
    'investigation→pending_evidence': { defaultAssignee: firstCo, observers: [] },
    'investigation→suspicion_formed': { defaultAssignee: firstCo, observers: mgrIds },
    'investigation→closed': { defaultAssignee: firstCo, observers: firstMgr ? [firstMgr] : [] },
    'pending_evidence→investigation': { defaultAssignee: firstCo, observers: [] },
    'pending_evidence→suspicion_formed': { defaultAssignee: firstCo, observers: mgrIds },
    'suspicion_formed→smr_draft': { defaultAssignee: firstCo, observers: firstMgr ? [firstMgr] : [] },
    'smr_draft→under_review': { defaultAssignee: firstMgr, observers: mgrIds.filter(id => id !== firstMgr) },
    'under_review→filed': { defaultAssignee: firstMgr, observers: mgrIds.filter(id => id !== firstMgr) },
    'under_review→smr_draft': { defaultAssignee: firstCo, observers: [] },
    'suspicion_formed→investigation': { defaultAssignee: firstCo, observers: mgrIds },
    'smr_draft→suspicion_formed': { defaultAssignee: firstCo, observers: firstMgr ? [firstMgr] : [] },
  }
}

const TRANSITION_LABELS = {
  'investigation→pending_evidence': 'Investigation → Pending Evidence',
  'investigation→suspicion_formed': 'Investigation → Suspicion Formed',
  'investigation→closed': 'Investigation → Close',
  'pending_evidence→investigation': 'Pending Evidence → Resume Investigation',
  'pending_evidence→suspicion_formed': 'Pending Evidence → Suspicion Formed',
  'suspicion_formed→smr_draft': 'Suspicion Formed → SMR Draft',
  'smr_draft→under_review': 'SMR Draft → SMR Approved',
  'under_review→filed': 'SMR Approved → SMR Filed',
  'under_review→smr_draft': 'SMR Approved → Return for Revision',
  'suspicion_formed→investigation': '↩ Reverse Suspicion (override)',
  'smr_draft→suspicion_formed': '↩ Return to Suspicion Formed (override)',
}

// Derive transition display labels from custom stage labels (used in Routing panel)
function getTransitionLabels(stageLabels) {
  const L = (id) => stageLabels?.[id] ?? STAGES.find(s => s.id === id)?.label ?? id
  return {
    'investigation→pending_evidence': `${L('investigation')} → ${L('pending_evidence')}`,
    'investigation→suspicion_formed': `${L('investigation')} → ${L('suspicion_formed')}`,
    'investigation→closed': `${L('investigation')} → Close`,
    'pending_evidence→investigation': `${L('pending_evidence')} → Resume Investigation`,
    'pending_evidence→suspicion_formed': `${L('pending_evidence')} → ${L('suspicion_formed')}`,
    'suspicion_formed→smr_draft': `${L('suspicion_formed')} → ${L('smr_draft')}`,
    'smr_draft→under_review': `${L('smr_draft')} → ${L('under_review')}`,
    'under_review→filed': `${L('under_review')} → ${L('filed')}`,
    'under_review→smr_draft': `${L('under_review')} → Return for Revision`,
    'suspicion_formed→investigation': `↩ Reverse Suspicion (override)`,
    'smr_draft→suspicion_formed': `↩ Return to ${L('suspicion_formed')} (override)`,
  }
}

const TRANSITION_RULES = {
  investigation: {
    forward: [
      { to: 'pending_evidence', label: 'Pause — Pending Evidence', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_identified', label: 'I have identified what evidence is outstanding' }, { id: 'request_sent', label: 'I have sent the request or notified the relevant party' }, { id: 'deadline_set', label: 'I have set a follow-up deadline' }], requiresReason: true, reasonLabel: 'What are you waiting for?', warning: false },
      { to: 'suspicion_formed', label: 'Form Suspicion — Start SMR Clock', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_assessed', label: 'I have reviewed all available evidence' }, { id: 'innocent_considered', label: 'I have considered and ruled out innocent explanations' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days). This record is permanent and cannot be deleted.' },
      { to: 'closed', label: 'Close — No Further Action', allowedRoles: ['co'], needsManagerApproval: true, conditions: [{ id: 'investigation_complete', label: 'I have completed and documented the investigation' }, { id: 'innocent_explanation', label: 'I have documented the innocent explanation' }], requiresReason: true, reasonLabel: 'Document the innocent explanation', warning: false },
    ],
    backward: [],
  },
  pending_evidence: {
    forward: [
      { to: 'investigation', label: 'Evidence Received — Resume', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_received', label: 'I confirm the requested evidence has been received' }], requiresReason: true, reasonLabel: 'What evidence was received?', warning: false },
      { to: 'suspicion_formed', label: 'Non-Response — Form Suspicion', allowedRoles: ['co', 'manager'], conditions: [{ id: 'timeout_elapsed', label: 'I confirm the deadline has passed without response' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days).', direction: 'forward' },
    ],
    backward: [{ to: 'investigation', label: 'Return to Investigation', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'Manager override.', direction: 'backward' }],
  },
  suspicion_formed: {
    forward: [{ to: 'smr_draft', label: 'Open SMR Draft', allowedRoles: ['co', 'manager'], conditions: [{ id: 'suspicion_documented', label: 'Suspicion grounds are recorded in the investigation record' }], requiresReason: false, direction: 'forward' }],
    backward: [{ to: 'investigation', label: 'Return to Investigation (Override)', allowedRoles: ['manager'], conditions: [{ id: 'legal_risk_noted', label: 'I acknowledge this is a high-risk override with legal implications' }], requiresReason: true, reasonLabel: 'Why reversing suspicion?', warning: true, warningText: 'CRITICAL. Reversing suspicion has legal implications. The SMR clock does not reset.', direction: 'backward' }],
  },
  smr_draft: {
    forward: [{ to: 'under_review', label: 'Submit for Manager Review', allowedRoles: ['co'], conditions: [{ id: 'narrative_complete', label: 'I have completed the SMR narrative' }, { id: 'evidence_referenced', label: 'I have referenced the supporting evidence in the narrative' }], requiresReason: true, reasonLabel: 'Note to manager', warning: false, direction: 'forward' }],
    backward: [{ to: 'suspicion_formed', label: 'Return to Suspicion Formed', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'SMR clock still running.', direction: 'backward' }],
  },
  under_review: {
    forward: [{ to: 'filed', label: 'Approve and Submit to AUSTRAC', allowedRoles: ['manager'], conditions: [{ id: 'narrative_reviewed', label: 'I have reviewed the full SMR narrative' }, { id: 'evidence_confirmed', label: 'I have confirmed the evidence supports the filing' }], requiresReason: true, reasonLabel: 'Manager approval note', warning: false, direction: 'forward' }],
    backward: [{ to: 'smr_draft', label: 'Request Revision from CO', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'What needs revision?', warning: true, warningText: 'Revision consumes SMR clock time.', direction: 'backward' }],
  },
  filed: { forward: [], backward: [] },
}

const SCREEN_MAP = {
  investigation: [
    { label: 'Open investigation record', desc: 'Evidence board, decision tree', icon: Search, primary: true },
    { label: 'View patron profile', desc: 'KYC, AML screening, risk history', icon: Eye, primary: false },
    { label: 'Review payout transactions', desc: 'Transaction log', icon: DollarSign, primary: false },
  ],
  pending_evidence: [
    { label: 'Update evidence items', desc: 'Mark received, log non-response', icon: Paperclip, primary: true },
    { label: 'View patron profile', desc: 'Check for new activity', icon: Eye, primary: false },
  ],
  suspicion_formed: [
    { label: 'Open SMR draft', desc: '3-day clock is running', icon: FileText, primary: true },
    { label: 'Open ECDD record', desc: 'Enhanced due diligence', icon: Shield, primary: false },
  ],
  smr_draft: [{ label: 'Continue SMR draft', desc: 'Narrative, evidence attachments', icon: FileText, primary: true }],
  under_review: [{ label: 'Open SMR for manager approval', desc: 'Approve and submit, or request revision', icon: Eye, primary: true }],
  filed: [
    { label: 'View filing record', desc: 'AUSTRAC reference', icon: FileText, primary: true, navigateTo: (c) => `/compliance-safe/smr-registry?caseId=${encodeURIComponent(c.id || c._dbId || '')}` },
    { label: 'Open audit log', desc: '7-year retention', icon: Lock, primary: false },
  ],
}

const PRIORITY_META = { critical: { color: C.red, label: 'Critical' }, high: { color: C.orange, label: 'High' }, medium: { color: C.amber, label: 'Medium' }, low: { color: C.green, label: 'Low' } }
/** Kanban stages omit “alert”; timeline shows Monitor Safe vs investigation as separate steps. */
const TIMELINE_STAGE_LABELS = {
  alert: 'Alert (Monitor Safe)',
}

function labelTimelineStage(stageId, stages = STAGES) {
  if (stageId == null || stageId === '') return null
  return stages.find((s) => s.id === stageId)?.label ?? TIMELINE_STAGE_LABELS[stageId] ?? stageId
}

const EVENT_META = {
  alert_fired: { color: C.red, label: 'Alert fired' },
  stage_change: { color: C.pri, label: 'Stage change' },
  assignment: { color: C.pri, label: 'Assignment' },
  evidence_added: { color: C.purple, label: 'Evidence added' },
  request_sent: { color: C.amber, label: 'Request sent' },
  suspicion: { color: C.red, label: 'Suspicion formed' },
  override: { color: C.red, label: 'Manager override' },
  note: { color: C.slate, label: 'Note' },
}

/* ─── MEMBER AVATAR + SELECTORS ─── */
function MemberAvatar({ member, size = 28 }) {
  if (!member) return null
  return (
    <div title={`${member.name} — ${member.title}`} style={{ width: size, height: size, borderRadius: '50%', background: member.color + '22', border: `2px solid ${member.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.32, fontWeight: 800, color: member.color, flexShrink: 0 }}>
      {member.initials}
    </div>
  )
}

function MemberSelector({ label, value, onChange, required, teamMembers = [] }) {
  const selected = teamMembers.find(m => m.id === value)
  return (
    <div>
      <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
        {label}{required && <span style={{ color: C.red }}> *</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: '100%', padding: '9px 34px 9px 38px', border: `1px solid ${value ? C.pri + '66' : C.border}`, borderRadius: 8, fontSize: T.normal, fontFamily: F.sans, color: value ? C.ink : C.slateL, background: '#fff', cursor: 'pointer', appearance: 'none' }}>
          <option value="">— Unassigned —</option>
          {teamMembers.map(m => (
            <option key={m.id} value={m.id}>{m.name} · {m.title}</option>
          ))}
        </select>
        <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          {selected ? <MemberAvatar member={selected} size={20} /> : <User size={14} color={C.slateL} />}
        </div>
        <ChevronDown size={13} color={C.slateL} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      </div>
    </div>
  )
}

function ObserverSelector({ label, values, onChange, teamMembers = [] }) {
  const toggleObserver = (id) => onChange(values.includes(id) ? values.filter(v => v !== id) : [...values, id])
  return (
    <div>
      <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
        {label} <span style={{ fontWeight: 400, textTransform: 'none' }}>— notified, view only</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {teamMembers.map(m => {
          const on = values.includes(m.id)
          return (
            <div key={m.id} onClick={() => toggleObserver(m.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: on ? m.color + '14' : C.slateBg, border: `1px solid ${on ? m.color + '55' : C.border}`, borderRadius: 20, cursor: 'pointer', transition: 'all 0.12s' }}>
              <MemberAvatar member={m} size={18} />
              <span style={{ fontSize: T.small, fontWeight: on ? 700 : 400, color: on ? m.color : C.slate }}>{m.name.split(' ')[0]}</span>
              {on && <X size={9} color={m.color} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StageLabelsModal({ stageLabels, onSaveStageLabels, onClose }) {
  const [editingLabels, setEditingLabels] = useState(() => {
    const labels = {}
    STAGES.forEach(s => { labels[s.id] = stageLabels?.[s.id] ?? s.label })
    return labels
  })
  const [savingLabels, setSavingLabels] = useState(false)
  useEffect(() => {
    const labels = {}
    STAGES.forEach(s => { labels[s.id] = stageLabels?.[s.id] ?? s.label })
    setEditingLabels(labels)
  }, [stageLabels])

  const handleSave = async () => {
    if (!onSaveStageLabels) return
    setSavingLabels(true)
    try {
      await onSaveStageLabels(editingLabels)
      onClose()
    } catch (e) {
      console.error('Failed to save stage labels', e)
    } finally {
      setSavingLabels(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: 420, maxWidth: '100%', background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', background: C.priD, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Type size={18} color="#7ec8e3" />
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Stage Labels</span>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}>
            <X size={16} color="#e0eff5" />
          </button>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <div style={{ fontSize: T.normal, color: C.slate, marginBottom: 16 }}>Customize display names for each pipeline stage.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {STAGES.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 130, fontSize: T.small, color: C.slateL }}>{s.id}</span>
                <input
                  type="text"
                  value={editingLabels[s.id] ?? s.label}
                  onChange={e => setEditingLabels(prev => ({ ...prev, [s.id]: e.target.value }))}
                  style={{ flex: 1, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: T.normal, fontFamily: F.sans }}
                  placeholder={s.label}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '8px 16px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: T.normal, fontWeight: 700, color: C.slate, cursor: 'pointer', fontFamily: F.sans }}>Cancel</button>
            <button onClick={handleSave} disabled={savingLabels} style={{ padding: '8px 16px', background: savingLabels ? C.slateL : C.pri, color: '#fff', border: 'none', borderRadius: 6, fontSize: T.normal, fontWeight: 700, cursor: savingLabels ? 'not-allowed' : 'pointer', fontFamily: F.sans }}>
              {savingLabels ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function SettingsPanel({ routing, onUpdate, onClose, stageLabels, teamMembers = [] }) {
  const [active, setActive] = useState(Object.keys(TRANSITION_LABELS)[0])
  const current = routing[active] || { defaultAssignee: '', observers: [] }
  const transitionLabels = getTransitionLabels(stageLabels)

  const updateCurrent = (patch) => onUpdate({ ...routing, [active]: { ...current, ...patch } })

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: 0 }}>
      <div style={{ width: 680, height: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.18)' }}>
        <div style={{ padding: '18px 22px', background: C.priD, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <Settings size={14} color="#7ec8e3" />
              <span style={{ fontSize: T.small, fontWeight: 700, color: '#7ec8e3', textTransform: 'uppercase', letterSpacing: 0.8 }}>Routing Settings</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Transition Defaults</div>
            <div style={{ fontSize: T.normal, color: '#a0c4d4', marginTop: 3 }}>Set who owns a case and who is notified when it moves between stages. Overridable at transition time.</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, padding: 7, cursor: 'pointer' }}>
            <X size={16} color="#e0eff5" />
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <div style={{ width: 240, borderRight: `1px solid ${C.border}`, overflowY: 'auto', background: C.slateBg, flexShrink: 0 }}>
            <div style={{ padding: '10px 14px', fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>Transitions</div>
            {Object.keys(TRANSITION_LABELS).map((key) => {
              const label = transitionLabels[key] ?? TRANSITION_LABELS[key]
              const isBack = label.startsWith('↩')
              return (
                <div key={key} onClick={() => setActive(key)} style={{ padding: '10px 14px', cursor: 'pointer', background: active === key ? C.priM : 'transparent', borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${active === key ? C.pri : 'transparent'}` }}>
                  {isBack && <div style={{ fontSize: T.small, fontWeight: 700, color: C.orange, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>Override</div>}
                  <div style={{ fontSize: T.normal, fontWeight: active === key ? 700 : 500, color: active === key ? C.pri : C.ink, lineHeight: 1.4 }}>{label.replace('↩ ', '')}</div>
                </div>
              )
            })}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink, marginBottom: 4 }}>{(transitionLabels[active] ?? TRANSITION_LABELS[active])?.replace('↩ ', '')}</div>
            {(transitionLabels[active] ?? TRANSITION_LABELS[active])?.startsWith('↩') && (
              <div style={{ fontSize: T.normal, color: C.orange, background: C.orangeBg, border: `1px solid ${C.orangeBd}`, borderRadius: 7, padding: '8px 14px', marginBottom: 14 }}>Manager override transition — assignment still applies</div>
            )}
            <div style={{ fontSize: T.normal, color: C.slateL, lineHeight: 1.5, marginBottom: 20 }}>When a case moves via this transition, it will be assigned to the default owner below and the listed observers will be notified. Both can be changed when confirming the transition.</div>
            <div style={{ marginBottom: 22 }}>
              <MemberSelector label="Default assignee — owns the case at next stage" value={current.defaultAssignee} onChange={v => updateCurrent({ defaultAssignee: v })} required teamMembers={teamMembers} />
              {current.defaultAssignee && (() => {
                const m = teamMembers.find(x => x.id === current.defaultAssignee)
                return m ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, padding: '8px 12px', background: m.color + '0d', border: `1px solid ${m.color}33`, borderRadius: 7 }}>
                    <MemberAvatar member={m} size={26} />
                    <div>
                      <div style={{ fontSize: T.normal, fontWeight: 700, color: C.ink }}>{m.name}</div>
                      <div style={{ fontSize: T.small, color: C.slateL }}>{m.title} · will be assigned ownership</div>
                    </div>
                  </div>
                ) : null
              })()}
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 22 }}>
              <ObserverSelector label="Observers" values={current.observers || []} onChange={v => updateCurrent({ observers: v })} teamMembers={teamMembers} />
              {(current.observers || []).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {(current.observers || []).map(id => {
                    const m = teamMembers.find(x => x.id === id)
                    if (!m) return null
                    return (
                      <div key={id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 12px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 7, marginBottom: 6 }}>
                        <MemberAvatar member={m} size={24} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: T.normal, fontWeight: 600, color: C.ink }}>{m.name}</div>
                          <div style={{ fontSize: T.small, color: C.slateL }}>{m.title} · notified, view only</div>
                        </div>
                        <button onClick={() => updateCurrent({ observers: (current.observers || []).filter(x => x !== id) })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, color: C.slateL }}>
                          <Trash2 size={13} color={C.slateL} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div style={{ background: C.priM, border: `1px solid ${C.pri}33`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: T.small, fontWeight: 700, color: C.pri, marginBottom: 4 }}>How this works</div>
              <div style={{ fontSize: T.normal, color: C.slate, lineHeight: 1.6 }}>These are defaults only. When anyone confirms this transition, the modal will pre-fill these values — but the person confirming can change the assignee or add/remove observers for that specific case before confirming.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── HELPERS ─── */
const now = () => new Date()
function timeAgo(date) {
  const d = date ? new Date(date) : null
  if (!d) return '—'
  const diff = (now() - d) / 1000
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}

/** Local date + time for audit-style display */
function formatDateTime(value) {
  if (value == null || value === '') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * Timestamp for "how long in this column" — avoids using generic updated_at for SMR filed/closed.
 * Filed: prefer resolved_at / regulatory_report_date (actual filing window).
 */
function pickStageEnteredAt(apiCase, stage, status) {
  const st = (status || 'INVESTIGATING').toUpperCase()
  if (stage === 'filed' || st === 'FILED') {
    return apiCase.resolved_at || apiCase.regulatory_report_date || apiCase.updated_at || apiCase.created_at
  }
  if (stage === 'under_review' && st === 'PENDING_APPROVAL') {
    return apiCase.updated_at || apiCase.created_at
  }
  if (stage === 'closed' && (st === 'RESOLVED' || st === 'CLOSED')) {
    return apiCase.closed_at || apiCase.resolved_at || apiCase.updated_at || apiCase.created_at
  }
  return apiCase.updated_at || apiCase.created_at
}

/** Legal “suspicion formed” moment for SMR: first transition into PENDING_REVIEW from investigation (or titled activity). */
function pickSuspicionFormedAt(apiCase, activities = []) {
  const ct = (apiCase?.case_type || '').toUpperCase()
  if (ct !== 'SMR' && ct !== 'INVESTIGATION') return null
  const list = Array.isArray(activities) ? [...activities] : []
  list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  for (const a of list) {
    const raw = String(a.activity_type || '').toUpperCase()
    const title = `${a.title || ''} ${a.description || ''}`
    if (/suspicion formed/i.test(title)) return a.created_at
    if (raw !== 'STATUS_CHANGE') continue
    const ov = parseActivityPayload(a.old_value)
    const nv = parseActivityPayload(a.new_value)
    if (nv?.status === 'PENDING_REVIEW' && ['INVESTIGATING', 'ESCALATED', 'OPEN'].includes(ov?.status)) {
      return a.created_at
    }
  }
  return null
}

function mergeTimelineEvents(events) {
  const arr = [...(events || [])].filter(e => e && e.at != null)
  arr.sort((a, b) => new Date(a.at) - new Date(b.at))
  return arr
}

/**
 * Time of PENDING_REVIEW → PENDING_APPROVAL (submit for manager) — end of SMR Draft phase.
 */
function findSmrSubmitForApprovalAt(activities = []) {
  let bestMs = null
  let bestAt = null
  for (const a of activities || []) {
    const raw = String(a.activity_type || '').toUpperCase()
    if (raw !== 'STATUS_CHANGE' || !a.created_at) continue
    const ov = parseActivityPayload(a.old_value)
    const nv = parseActivityPayload(a.new_value)
    if (nv?.status === 'PENDING_APPROVAL' && ov?.status === 'PENDING_REVIEW') {
      const m = new Date(a.created_at).getTime()
      if (bestMs == null || m < bestMs) {
        bestMs = m
        bestAt = a.created_at
      }
    }
  }
  return bestAt
}

/**
 * SMR: “Suspicion formed” and “SMR Draft” are both PENDING_REVIEW in the API, so there is often no STATUS_CHANGE
 * when the CO moves from forming suspicion to drafting the SMR. Inject a single synthetic step so the timeline
 * shows Suspicion formed → SMR Draft → (existing) SMR Draft → SMR Approved.
 *
 * Timestamp: between suspicion and “submit for approval” when that activity exists; otherwise prior heuristics.
 */
function pickSmrDraftEnteredAt(apiCase, suspicionFormedAt, activities = []) {
  if (!suspicionFormedAt || !apiCase) return null
  const susMs = new Date(suspicionFormedAt).getTime()
  const submitAt = findSmrSubmitForApprovalAt(activities)
  if (submitAt) {
    const subMs = new Date(submitAt).getTime()
    if (subMs > susMs) {
      const mid = susMs + Math.max(1, Math.floor((subMs - susMs) / 2))
      return new Date(mid).toISOString()
    }
  }
  const upd = apiCase.updated_at
  if (upd && new Date(upd).getTime() > susMs) return upd
  const list = Array.isArray(activities) ? [...activities] : []
  list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  for (const a of list) {
    if (!a.created_at) continue
    const m = new Date(a.created_at).getTime()
    if (m > susMs) return a.created_at
  }
  return new Date(susMs + 1).toISOString()
}

/** Inject synthetic draft row when still PENDING_REVIEW, or past that (approval/filed), or closed after submit-for-approval. */
function shouldInjectSmrDraftStep(apiCase, activities) {
  const st = (apiCase?.status || '').toUpperCase()
  if (st === 'PENDING_REVIEW' || st === 'PENDING_APPROVAL' || st === 'FILED') return true
  if (['RESOLVED', 'CLOSED'].includes(st) && findSmrSubmitForApprovalAt(activities)) return true
  return false
}

function enrichSmrDraftAfterSuspicion(timeline, apiCase) {
  const ct = (apiCase?.case_type || '').toUpperCase()
  const activities = apiCase?.activities || []
  if ((ct !== 'SMR' && ct !== 'INVESTIGATION') || !shouldInjectSmrDraftStep(apiCase, activities)) return timeline
  const suspicionAt = pickSuspicionFormedAt(apiCase, activities)
  if (!suspicionAt) return timeline

  const t = timeline || []
  if (t.some(ev => ev.from === 'suspicion_formed' && ev.to === 'smr_draft')) return timeline

  const draftAt = pickSmrDraftEnteredAt(apiCase, suspicionAt, activities)
  if (!draftAt) return timeline

  let draftActor = apiCase.assigned_to || apiCase.updated_by
  if (!draftActor || String(draftActor).trim() === '') {
    const sus = (activities || []).find(
      (x) => /suspicion formed/i.test(`${x?.title || ''} ${x?.description || ''}`)
    )
    if (sus) draftActor = pickActorFromActivity(sus)
  }
  if (!draftActor || String(draftActor).trim() === '') draftActor = 'Unknown user'

  return mergeTimelineEvents([
    ...t,
    {
      type: 'stage_change',
      at: draftAt,
      actor: draftActor,
      from: 'suspicion_formed',
      to: 'smr_draft',
      note: 'SMR Draft — preparing the report. Regulatory status remains PENDING_REVIEW until submitted for manager approval.',
    },
  ])
}

// SMR clock: 3 business days from suspicion_formed_at
function smrDeadline(suspicionFormedAt) {
  if (!suspicionFormedAt) return null
  const d = new Date(suspicionFormedAt)
  if (isNaN(d)) return null
  // Add 3 business days (skip weekends)
  let added = 0
  const deadline = new Date(d)
  while (added < 3) {
    deadline.setDate(deadline.getDate() + 1)
    const dow = deadline.getDay()
    if (dow !== 0 && dow !== 6) added++
  }
  // Set to end of business day (17:00 local)
  deadline.setHours(17, 0, 0, 0)
  const msLeft = deadline - now()
  const hoursLeft = msLeft / (1000 * 60 * 60)
  const daysLeft = Math.ceil(hoursLeft / 24)
  if (msLeft <= 0) return { label: 'OVERDUE', daysLeft: 0, overdue: true, deadline }
  if (hoursLeft <= 24) return { label: `${Math.ceil(hoursLeft)}h left`, daysLeft: 0, overdue: false, urgent: true, deadline }
  return { label: `${daysLeft}d left`, daysLeft, overdue: false, urgent: daysLeft <= 1, deadline }
}

function mapApiCaseToPipeline(apiCase) {
  const status = (apiCase.status || 'INVESTIGATING').toUpperCase()
  const caseType = String(apiCase.case_type || 'INVESTIGATION').toUpperCase()
  const stage = resolvePipelineStage(apiCase)
  const patronName = resolvePatronListTitle(apiCase)
  const patronIdLine = resolvePatronListSecondaryId(apiCase) || '—'
  const caseId = apiCase.case_number || apiCase.id
  const risk = apiCase.risk_score ?? 50
  const priority = (apiCase.priority || 'medium').toLowerCase()
  const assigneeName = apiCase.assigned_to || 'Unassigned'
  const initials = assigneeName.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase() || '—'
  const timeline = enrichTimelineFromCase(
    mapApiActivitiesToTimeline(apiCase.activities || [], caseType, apiCase),
    apiCase
  )
  const suspicionFormedAt = apiCase.suspicion_formed_at || pickSuspicionFormedAt(apiCase, apiCase.activities || [])
  return {
    id: caseId,
    _dbId: apiCase.id,
    _caseType: caseType,
    stage,
    _apiStatus: status,
    priority,
    patron: { id: patronIdLine, name: patronName, risk },
    alert: { id: apiCase.source_alert_id || '—', rule: apiCase.rule_name || '—', typology: apiCase.category || '—' },
    assignee: { name: assigneeName, role: 'co', initials },
    stageEnteredAt: pickStageEnteredAt(apiCase, stage, status),
    suspicionFormedAt,
    investigationProgress: apiCase.progress,
    pendingItems: apiCase.progress < 100 ? [`${apiCase.steps_done || 0}/${apiCase.steps_total || 0} steps`] : [],
    timeline,
  }
}

function mapApiActivitiesToTimeline(activities = [], caseType, apiCase = null) {
  const ct = String(caseType || '').toUpperCase()
  const fromAlertSource =
    apiCase &&
    (apiCase.source_alert_id != null ||
      (apiCase.rule_name != null && String(apiCase.rule_name).trim() !== ''))
  return (Array.isArray(activities) ? activities : []).map((a) => {
    const oldVal = parseActivityPayload(a.old_value)
    const newVal = parseActivityPayload(a.new_value)
    const raw = String(a.activity_type || '').toUpperCase()
    let from = oldVal?.status ? apiStatusToTimelineStage(oldVal.status) : null
    let to = newVal?.status ? apiStatusToTimelineStage(newVal.status) : null
    let type = 'note'
    if (raw === 'STATUS_CHANGE') type = 'stage_change'
    else if (raw === 'ASSIGNMENT') type = 'assignment'
    if (/suspicion formed/i.test(a.title || '') || /suspicion formed/i.test(a.description || '')) type = 'suspicion'
    // Human hand-off: alert queue → investigation (OPEN/INVESTIGATING), not the same event as “alert fired”.
    if (
      fromAlertSource &&
      raw === 'STATUS_CHANGE' &&
      !oldVal?.status &&
      newVal?.status &&
      ['OPEN', 'INVESTIGATING'].includes(String(newVal.status).toUpperCase())
    ) {
      from = 'alert'
    }
    // OPEN and INVESTIGATING both map to Investigation — not a second pipeline move.
    if (raw === 'STATUS_CHANGE' && from != null && to != null && from === to) {
      type = 'note'
      from = null
      to = null
    }
    return {
      type,
      at: a.created_at,
      actor: pickActorFromActivity(a),
      note: [a.title, a.description].filter(Boolean).join(' — ') || '—',
      from,
      to,
    }
  })
}

function enrichTimelineFromCase(timeline, apiCase) {
  const t = [...(timeline || [])]
  if (!apiCase) return mergeTimelineEvents(t)
  const hasAlertEvent = t.some(ev => ev.type === 'alert_fired' || (ev.note && /monitoring alert:/i.test(ev.note)))
  if (!hasAlertEvent && (apiCase.source_alert_id || apiCase.rule_name)) {
    const alertEv = {
      type: 'alert_fired',
      at: apiCase.created_at,
      actor: 'System',
      note: apiCase.rule_name
        ? `Monitoring alert: ${apiCase.rule_name}${apiCase.source_alert_id != null && String(apiCase.source_alert_id) !== '' ? ` (alert #${apiCase.source_alert_id})` : ''}`
        : `Alert raised${apiCase.source_alert_id != null ? ` (alert #${apiCase.source_alert_id})` : ''}`,
      from: null,
      to: 'alert',
    }
    t.push(alertEv)
  }
  let merged = mergeTimelineEvents(t)
  merged = enrichSmrDraftAfterSuspicion(merged, apiCase)
  return merged
}

/* ─── SUB-COMPONENTS ─── */
function CaseCard({ c, onSelect, isSelected, compact, stages = STAGES }) {
  const stage = stages.find(s => s.id === c.stage)
  const pm = PRIORITY_META[c.priority] || PRIORITY_META.medium
  const baseStyle = {
    background: isSelected ? C.priM : '#fff',
    border: `1px solid ${isSelected ? C.pri : C.border}`,
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 8,
    transition: 'all 0.15s',
    boxShadow: isSelected ? `0 0 0 2px ${C.pri}22` : '0 1px 3px rgba(0,0,0,0.04)',
  }
  if (compact) {
    return (
      <div onClick={() => onSelect(c)} style={{ ...baseStyle, padding: '8px 10px' }}>
        <div style={{ fontSize: T.normal, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{c.patron.name}</div>
        <div style={{ fontSize: T.small, fontFamily: F.mono, color: C.pri }}>{c.id}</div>
        <div style={{ fontSize: T.small, fontFamily: F.mono, color: C.slateL }}>{c.patron.id}</div>
      </div>
    )
  }
  return (
    <div onClick={() => onSelect(c)} style={{ ...baseStyle, padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
        <div>
          <div style={{ fontSize: T.normal, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{c.patron.name}</div>
          <div style={{ fontSize: T.small, fontFamily: F.mono, color: C.pri, marginBottom: 2 }}>{c.id}</div>
          <div style={{ fontSize: T.small, fontFamily: F.mono, color: C.slateL }}>{c.patron.id}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: stage?.bg || C.slateBg, border: `1px solid ${(stage?.color || C.slate)}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.small, fontWeight: 800, color: stage?.color || C.slate }}>
            {c.assignee.initials}
          </div>
          {c.assignee.name !== 'Unassigned' && (
            <div style={{ fontSize: T.small, color: C.slateL }}>{String(c.assignee.name).split(' ')[0]}</div>
          )}
        </div>
      </div>
      <div style={{ fontSize: T.small, color: C.slate, background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 7px', marginBottom: 7, display: 'inline-block' }}>
        {c.alert.id} · {c.alert.typology}
      </div>
      {c.investigationProgress !== undefined && (
        <div style={{ marginBottom: 7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.small, color: C.slateL, marginBottom: 2 }}>
            <span>Progress</span>
            <span>{c.investigationProgress}%</span>
          </div>
          <div style={{ height: 3, background: C.border, borderRadius: 2 }}>
            <div style={{ height: '100%', width: `${c.investigationProgress}%`, background: C.pri, borderRadius: 2 }} />
          </div>
        </div>
      )}
      {['suspicion_formed', 'smr_draft', 'under_review'].includes(c.stage) && c.suspicionFormedAt && (() => {
        const dl = smrDeadline(c.suspicionFormedAt)
        if (!dl) return null
        const bg = dl.overdue ? C.redBg : dl.urgent ? C.amberBg : C.priM
        const bd = dl.overdue ? C.redBd : dl.urgent ? C.amberBd : `${C.pri}33`
        const fg = dl.overdue ? C.red : dl.urgent ? C.amber : C.pri
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: bg, border: `1px solid ${bd}`, borderRadius: 5, marginBottom: 7, fontSize: T.small }}>
            <Clock size={11} color={fg} />
            <span style={{ fontWeight: 700, color: fg }}>SMR {dl.label}</span>
          </div>
        )
      })()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: pm.color }} />
          <span style={{ fontSize: T.small, color: pm.color, fontWeight: 600 }}>{pm.label}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, textAlign: 'right' }}>
          <span style={{ fontSize: T.small, fontFamily: F.mono, color: C.ink }} title="Local date and time">{formatDateTime(c.stageEnteredAt)}</span>
          <span style={{ fontSize: T.small, color: C.slateL }}>{timeAgo(c.stageEnteredAt)}</span>
        </div>
      </div>
      {(c._caseType === 'SMR' || c._caseType === 'INVESTIGATION') && c.suspicionFormedAt && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: T.small, fontWeight: 700, color: C.red, marginBottom: 2 }}>Suspicion formed</div>
          <div style={{ fontSize: T.small, fontFamily: F.mono, color: C.ink }} title="Legal clock start — local date and time">{formatDateTime(c.suspicionFormedAt)}</div>
          <div style={{ fontSize: T.small, color: C.slateL }}>{timeAgo(c.suspicionFormedAt)}</div>
        </div>
      )}
    </div>
  )
}

/** Skip transitions whose target API status already matches the case (stale UI column vs DB). */
function ruleChangesApiStatus(rule, currentApiStatus) {
  const target = STAGE_TO_STATUS[rule.to]
  if (!target) return true
  return String(target).toUpperCase() !== String(currentApiStatus || '').toUpperCase()
}

function CaseDrawer({ c, currentRole, onClose, onTransition, onOpenCase, onNavigate, stages = STAGES }) {
  const [activeTab, setActiveTab] = useState('transitions')
  const stage = stages.find(s => s.id === c.stage)
  const stageIndex = stages.findIndex(s => s.id === c.stage)
  const rawRules = TRANSITION_RULES[c.stage] || { forward: [], backward: [] }
  const apiSt = c._apiStatus
  const stageRules = {
    forward: (rawRules.forward || []).filter((r) => ruleChangesApiStatus(r, apiSt)),
    backward: (rawRules.backward || []).filter((r) => ruleChangesApiStatus(r, apiSt)),
  }
  const screens = SCREEN_MAP[c.stage] || []

  const RoleTag = ({ roles }) => (
    <div style={{ display: 'flex', gap: 4 }}>
      {roles.map(r => (
        <span key={r} style={{ fontSize: T.small, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: r === 'manager' ? C.purpleBg : C.priM, color: r === 'manager' ? C.purple : C.pri, border: `1px solid ${r === 'manager' ? C.purpleBd : C.pri + '44'}` }}>
          {r === 'manager' ? 'Manager' : 'CO'}
        </span>
      ))}
    </div>
  )

  return (
    <div style={{ width: 460, background: '#fff', borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', height: '100%', flexShrink: 0 }}>
      <div style={{ padding: '16px 20px', background: C.priD, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{c.patron.name}</div>
            <div style={{ fontFamily: F.mono, fontSize: T.small, color: '#7ec8e3', marginBottom: 3 }}>{c.id}</div>
            <div style={{ fontSize: T.normal, color: '#a0c4d4' }}>{c.patron.id} · Risk <strong style={{ color: '#fbbf24' }}>{c.patron.risk}</strong>/100</div>
            {(c._caseType === 'SMR' || c._caseType === 'INVESTIGATION') && c.suspicionFormedAt && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(220,38,38,0.12)', borderRadius: 6, border: '1px solid rgba(220,38,38,0.25)' }}>
                <div style={{ fontSize: T.small, fontWeight: 700, color: '#fecaca', textTransform: 'uppercase', letterSpacing: 0.4 }}>Suspicion formed</div>
                <div style={{ fontSize: T.normal, fontWeight: 700, color: '#fff' }}>{formatDateTime(c.suspicionFormedAt)}</div>
                <div style={{ fontSize: T.small, color: '#a0c4d4' }}>{timeAgo(c.suspicionFormedAt)} · SMR clock start</div>
              </div>
            )}
            {(() => {
              const aid = c.alert?.id
              const show = aid != null && String(aid) !== '' && String(aid) !== '—'
              if (!show) return null
              return (
                <div style={{ marginTop: 10 }}>
                  <Link
                    to={`/monitor-safe/alerts/${encodeURIComponent(aid)}?from=pipeline`}
                    style={{ fontSize: T.small, color: '#7ec8e3', fontWeight: 700, textDecoration: 'underline' }}
                  >
                    View source alert →
                  </Link>
                </div>
              )
            })()}
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}>
            <X size={16} color="#e0eff5" />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          {stages.map((s, i) => (
            <div key={s.id} style={{ flex: 1 }}>
              <div style={{ height: 3, borderRadius: 2, background: i < stageIndex ? '#86efac' : i === stageIndex ? '#7ec8e3' : 'rgba(255,255,255,0.15)' }} />
              {i === stageIndex && <div style={{ fontSize: T.small, color: '#7ec8e3', fontWeight: 700, marginTop: 2, textAlign: 'center' }}>{s.label}</div>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, background: C.slateBg, flexShrink: 0 }}>
        {[
          { id: 'transitions', label: 'Transitions' },
          { id: 'timeline', label: 'Timeline' },
          { id: 'screens', label: 'Open In' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab.id ? C.pri : 'transparent'}`, fontSize: T.normal, fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? C.pri : C.slate, cursor: 'pointer', fontFamily: F.sans }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {activeTab === 'transitions' && (
          <div>
            {stageRules.forward.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ArrowRight size={12} color={C.pri} /> Forward transitions
                </div>
                {stageRules.forward.map((rule, i) => {
                  const allowed = rule.allowedRoles.includes(currentRole)
                  const toStage = stages.find(s => s.id === rule.to)
                  return (
                    <div key={i} style={{ border: `1px solid ${allowed ? (rule.warning ? C.amberBd : C.border) : C.border}`, borderRadius: 9, marginBottom: 10, overflow: 'hidden', opacity: allowed ? 1 : 0.7 }}>
                      <div style={{ padding: '11px 14px', background: allowed ? (rule.warning ? C.amberBg : '#fafbfc') : C.slateBg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: T.normal, fontWeight: 700, color: allowed ? C.ink : C.slateL }}>{rule.label}</div>
                          {toStage && <div style={{ fontSize: T.small, color: C.slateL, marginTop: 2 }}>→ {toStage.label}</div>}
                        </div>
                        <RoleTag roles={rule.allowedRoles} />
                      </div>
                      <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: T.small, color: C.slate }}>{rule.conditions.length} condition(s) · {rule.requiresReason ? 'reason required' : 'no reason'}</div>
                        <button onClick={() => allowed && onTransition({ ...rule, direction: 'forward' })} disabled={!allowed} style={{ padding: '6px 16px', background: allowed ? C.pri : '#e2e8f0', color: allowed ? '#fff' : C.slateL, border: 'none', borderRadius: 6, fontSize: T.small, fontWeight: 700, cursor: allowed ? 'pointer' : 'not-allowed', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 5 }}>
                          {allowed ? <><ArrowRight size={11} /> Proceed</> : <>Locked</>}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {stageRules.backward.length > 0 && (
              <div>
                <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ArrowLeft size={12} color={C.orange} /> Return / override
                </div>
                {stageRules.backward.map((rule, i) => {
                  const allowed = rule.allowedRoles.includes(currentRole)
                  return (
                    <div key={i} style={{ border: `1px solid ${C.orangeBd}`, borderRadius: 9, marginBottom: 8, overflow: 'hidden', opacity: allowed ? 1 : 0.6 }}>
                      <div style={{ padding: '11px 14px', background: C.orangeBg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div><div style={{ fontSize: T.normal, fontWeight: 700, color: C.orange }}>{rule.label}</div></div>
                        <RoleTag roles={rule.allowedRoles} />
                      </div>
                      <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: T.small, color: C.slate }}>Permanent audit record</div>
                        <button onClick={() => allowed && onTransition({ ...rule, direction: 'backward' })} disabled={!allowed} style={{ padding: '6px 14px', background: allowed ? C.orange : '#e2e8f0', color: allowed ? '#fff' : C.slateL, border: 'none', borderRadius: 6, fontSize: T.small, fontWeight: 700, cursor: allowed ? 'pointer' : 'not-allowed', fontFamily: F.sans }}>
                          {allowed ? <><ArrowLeft size={11} /> Return</> : <>Locked</>}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {stageRules.forward.length === 0 && stageRules.backward.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.slateL }}>
                <CheckCircle2 size={24} color={C.green} style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: T.normal, fontWeight: 700, color: C.green, marginBottom: 4 }}>Terminal stage</div>
                <div style={{ fontSize: T.normal }}>This case has been filed.</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div>
            <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Case history</div>
            {(c.timeline && c.timeline.length > 0) ? (
              [...c.timeline].reverse().map((ev, i, arr) => {
                const em = EVENT_META[ev.type] || EVENT_META.note
                return (
                  <div key={i} style={{ display: 'flex', gap: 11, paddingBottom: i < arr.length - 1 ? 14 : 0, marginBottom: i < arr.length - 1 ? 14 : 0, borderBottom: i < arr.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: em.color, marginTop: 3 }} />
                      {i < arr.length - 1 && <div style={{ width: 1, flex: 1, background: '#f1f5f9', marginTop: 4 }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 2 }}>
                        <span style={{ fontSize: T.small, fontWeight: 700, color: em.color, textTransform: 'uppercase' }}>{em.label}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0, textAlign: 'right' }}>
                          <span style={{ fontSize: T.small, fontFamily: F.mono, color: C.ink }} title="Local date and time">{formatDateTime(ev.at)}</span>
                          <span style={{ fontSize: T.small, color: C.slateL }}>{timeAgo(ev.at)}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: T.small, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
                        Performed by: {ev.actor != null && String(ev.actor).trim() !== '' ? ev.actor : 'Unknown user'}
                      </div>
                      {ev.from && ev.to && (
                        <div style={{ fontSize: T.small, fontWeight: 700, color: C.pri, marginBottom: 6 }}>
                          {labelTimelineStage(ev.from, stages)}
                          <span style={{ color: C.slateL, fontWeight: 500 }}> → </span>
                          {labelTimelineStage(ev.to, stages)}
                        </div>
                      )}
                      {!ev.from && ev.to && (
                        <div style={{ fontSize: T.small, fontWeight: 700, color: C.pri, marginBottom: 6 }}>
                          <span style={{ color: C.slateL, fontWeight: 500 }}>→ </span>
                          {labelTimelineStage(ev.to, stages)}
                        </div>
                      )}
                      <div style={{ fontSize: T.normal, color: C.ink, lineHeight: 1.5 }}>{ev.note}</div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div style={{ color: C.slateL, fontSize: T.normal }}>No activity recorded yet.</div>
            )}
          </div>
        )}

        {activeTab === 'screens' && (
          <div>
            <div style={{ fontSize: T.normal, color: C.slateL, marginBottom: 14 }}>Continue working on this case.</div>
            {screens.map((s, i) => (
              <div
                key={i}
                onClick={() => {
                  if (s.navigateTo && onNavigate) onNavigate(s.navigateTo(c))
                  else if (onOpenCase) onOpenCase(c)
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 15px', background: s.primary ? C.priD : '#fff', border: `1px solid ${s.primary ? C.priD : C.border}`, borderRadius: 9, marginBottom: 9, cursor: 'pointer', transition: 'box-shadow 0.12s', boxShadow: s.primary ? '0 2px 8px rgba(10,81,105,0.18)' : 'none' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, background: s.primary ? 'rgba(255,255,255,0.12)' : C.priM, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <s.icon size={16} color={s.primary ? '#7ec8e3' : C.pri} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: T.normal, fontWeight: 700, color: s.primary ? '#fff' : C.ink, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: T.small, color: s.primary ? '#a0c4d4' : C.slateL, lineHeight: 1.4 }}>{s.desc}</div>
                </div>
                <ExternalLink size={13} color={s.primary ? '#7ec8e3' : C.slateL} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── TRANSITION MODAL (with routing defaults) ─── */
function TransitionModal({ caseData, rule, currentRole, routing = {}, onConfirm, onCancel, stages = STAGES, teamMembers = [] }) {
  const [checked, setChecked] = useState({})
  const [reason, setReason] = useState('')
  const routingKey = `${caseData.stage}→${rule.to}`
  const defaults = routing[routingKey] || { defaultAssignee: '', observers: [] }
  const [assignee, setAssignee] = useState(defaults.defaultAssignee || '')
  const [observers, setObservers] = useState(defaults.observers || [])
  // SVCP §9 suspicion gate — only shown when entering the SUSPICION_FORMED stage.
  // Backend rejects the transition unless all four acks are true + reason ≥ 20 chars.
  const isSuspicionTransition = rule.to === 'suspicion_formed'
  const [suspicionAcks, setSuspicionAcks] = useState({
    facts_documented: false,
    innocent_explanation_considered: false,
    innocent_explanation_ruled_out: false,
    reasonable_grounds_objective_test: false,
  })
  const allSuspicionAcksChecked = [
    'facts_documented',
    'innocent_explanation_considered',
    'innocent_explanation_ruled_out',
    'reasonable_grounds_objective_test',
  ].every((k) => suspicionAcks[k])
  // Filing details — collected only when moving to the "filed" stage. Backend requires
  // austrac_reference + filing_method + filed_at for any → FILED transition.
  const isFilingTransition = rule.to === 'filed'
  const [filingMethod, setFilingMethod] = useState('manual')
  const [austracReference, setAustracReference] = useState('')
  const [filedAtLocal, setFiledAtLocal] = useState(() => {
    const d = new Date()
    d.setSeconds(0, 0)
    // yyyy-MM-ddTHH:mm for <input type=datetime-local>
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  })
  const [filingNotes, setFilingNotes] = useState('')
  // SVCP §5.2 — structured decision reasoning on close. Gate fires for any
  // CLOSED transition that comes out of a post-OPEN stage (backend gate matches).
  const isClosingAfterInvestigation =
    rule.to === 'closed' && caseData.stage && caseData.stage !== 'open'
  const [closeResolution, setCloseResolution] = useState('')
  const canAct = rule.allowedRoles.includes(currentRole)
  const allConditionsMet = rule.conditions.every(c => checked[c.id])
  const reasonOk = (!rule.requiresReason && !isClosingAfterInvestigation) || reason.trim().length >= 20
  const filingOk = !isFilingTransition || (austracReference.trim() && filedAtLocal && filingMethod)
  const suspicionOk = !isSuspicionTransition || allSuspicionAcksChecked
  const closeOk = !isClosingAfterInvestigation || !!closeResolution
  const canConfirm = canAct && allConditionsMet && reasonOk && filingOk && suspicionOk && closeOk
  const toStage = stages.find(s => s.id === rule.to)
  const isBackward = rule.direction === 'backward'
  const assigneeMember = teamMembers.find(m => m.id === assignee)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '18px 22px', background: isBackward ? C.orangeBg : C.priD, borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              {isBackward ? <ArrowLeft size={14} color={C.orange} /> : <ArrowRight size={14} color="#7ec8e3" />}
              <span style={{ fontSize: T.small, fontWeight: 700, color: isBackward ? C.orange : '#7ec8e3', textTransform: 'uppercase', letterSpacing: 0.8 }}>{isBackward ? 'Stage Return' : 'Stage Transition'}</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: isBackward ? C.ink : '#fff' }}>{rule.label}</div>
            {toStage && (
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 5 }}>
                <span style={{ fontSize: T.normal, color: isBackward ? C.slate : '#a0c4d4' }}>→ Moving to:</span>
                <span style={{ fontSize: T.small, fontWeight: 700, color: isBackward ? toStage.color : '#fff', background: isBackward ? toStage.bg : 'rgba(255,255,255,0.15)', padding: '2px 10px', borderRadius: 20 }}>{toStage?.label || rule.to}</span>
              </div>
            )}
          </div>
          <button onClick={onCancel} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><X size={16} color={isBackward ? C.slate : '#e0eff5'} /></button>
        </div>
        <div style={{ padding: '18px 22px' }}>
          {/* Permission check */}
          <div style={{ padding: '10px 14px', background: canAct ? C.greenBg : C.redBg, border: `1px solid ${canAct ? C.greenBd : C.redBd}`, borderRadius: 8, marginBottom: 16, display: 'flex', gap: 9, alignItems: 'center' }}>
            {canAct ? <CheckCircle2 size={15} color={C.green} /> : <Lock size={15} color={C.red} />}
            <div>
              <div style={{ fontSize: T.normal, fontWeight: 700, color: canAct ? C.green : C.red }}>{canAct ? `Permitted — ${currentRole === 'manager' ? 'Compliance Manager' : 'Compliance Officer'}` : 'Not permitted for your role'}</div>
              <div style={{ fontSize: T.small, color: C.slate }}>{canAct ? `Allowed roles: ${rule.allowedRoles.map(r => r === 'manager' ? 'Manager' : 'CO').join(' · ')}` : `Only ${rule.allowedRoles.map(r => r === 'manager' ? 'Compliance Manager' : 'Compliance Officer').join(' or ')} can perform this transition`}</div>
            </div>
          </div>
          {rule.warning && (
            <div style={{ padding: '12px 14px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderLeft: `4px solid ${C.amber}`, borderRadius: 8, marginBottom: 16, display: 'flex', gap: 9 }}>
              <AlertTriangle size={15} color={C.amber} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: T.normal, color: '#78350f', lineHeight: 1.6 }}>{rule.warningText}</div>
            </div>
          )}
          {rule.needsManagerApproval && (
            <div style={{ padding: '10px 14px', background: C.purpleBg, border: `1px solid ${C.purpleBd}`, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: T.normal, fontWeight: 700, color: C.purple, marginBottom: 2 }}>Requires manager approval</div>
              <div style={{ fontSize: T.small, color: C.slate }}>This transition will be flagged for Compliance Manager sign-off before taking effect.</div>
            </div>
          )}
          {rule.conditions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Conditions — all must be confirmed</div>
              {rule.conditions.map(cond => (
                <div key={cond.id} onClick={() => canAct && setChecked(prev => ({ ...prev, [cond.id]: !prev[cond.id] }))} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 12px', marginBottom: 7, background: checked[cond.id] ? C.greenBg : C.slateBg, border: `1px solid ${checked[cond.id] ? C.greenBd : C.border}`, borderRadius: 8, cursor: canAct ? 'pointer' : 'not-allowed', transition: 'all 0.12s' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, background: checked[cond.id] ? C.green : '#fff', border: `2px solid ${checked[cond.id] ? C.green : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{checked[cond.id] && <CheckSquare size={11} color="#fff" />}</div>
                  <span style={{ fontSize: T.normal, color: checked[cond.id] ? C.green : C.ink, fontWeight: checked[cond.id] ? 600 : 400, lineHeight: 1.4 }}>{cond.label}</span>
                </div>
              ))}
            </div>
          )}
          {/* Assignment & Notification */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 12 }}>
              <UserCheck size={13} color={C.pri} />
              <span style={{ fontSize: T.small, fontWeight: 700, color: C.pri, textTransform: 'uppercase', letterSpacing: 0.5 }}>Assignment &amp; Notification</span>
              {defaults.defaultAssignee && <span style={{ fontSize: T.small, color: C.slateL, background: C.slateBg, padding: '2px 8px', borderRadius: 10, border: `1px solid ${C.border}` }}>pre-filled from routing settings</span>}
            </div>
            <div style={{ marginBottom: 14 }}>
              <MemberSelector label="Assign to — owns the case at next stage" value={assignee} onChange={setAssignee} required teamMembers={teamMembers} />
              {assigneeMember && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, padding: '7px 11px', background: assigneeMember.color + '0d', border: `1px solid ${assigneeMember.color}33`, borderRadius: 7 }}>
                  <MemberAvatar member={assigneeMember} size={24} />
                  <div>
                    <div style={{ fontSize: T.normal, fontWeight: 700, color: C.ink }}>{assigneeMember.name}</div>
                    <div style={{ fontSize: T.small, color: C.slateL }}>{assigneeMember.title}</div>
                  </div>
                </div>
              )}
            </div>
            <ObserverSelector label="Notify" values={observers} onChange={setObservers} teamMembers={teamMembers} />
            {observers.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                {observers.map(id => {
                  const m = teamMembers.find(x => x.id === id)
                  return m ? (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: m.color + '0d', border: `1px solid ${m.color}33`, borderRadius: 20 }}>
                      <MemberAvatar member={m} size={16} />
                      <span style={{ fontSize: T.small, color: m.color, fontWeight: 600 }}>{m.name.split(' ')[0]}</span>
                      <span style={{ fontSize: T.small, color: C.slateL }}>· notified</span>
                    </div>
                  ) : null
                })}
              </div>
            )}
          </div>
          {isSuspicionTransition && (
            <div style={{ marginBottom: 18, padding: '14px 16px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderLeft: `4px solid ${C.amber}`, borderRadius: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <AlertTriangle size={14} color={C.amber} />
                <span style={{ fontSize: T.small, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', letterSpacing: 0.5 }}>Suspicion Formation Gate — SVCP §9</span>
              </div>
              <div style={{ fontSize: T.small, color: '#78350f', lineHeight: 1.5, marginBottom: 10 }}>
                Reasonable grounds to suspect a designated service is being used for an offence must be objectively defensible. Tick each only if you can truthfully confirm it — this record is persisted to the case and cannot be edited after filing.
              </div>
              {[
                { key: 'facts_documented', label: 'I have documented the specific facts I observed.' },
                { key: 'innocent_explanation_considered', label: 'I have considered and documented an innocent explanation.' },
                { key: 'innocent_explanation_ruled_out', label: 'I have documented why that innocent explanation is ruled out.' },
                { key: 'reasonable_grounds_objective_test', label: 'An objective person with this information would also form suspicion — these are reasonable grounds, not mere suspicion.' },
              ].map((ack) => (
                <div
                  key={ack.key}
                  onClick={() => canAct && setSuspicionAcks((prev) => ({ ...prev, [ack.key]: !prev[ack.key] }))}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', marginBottom: 6, background: suspicionAcks[ack.key] ? C.greenBg : '#fff', border: `1px solid ${suspicionAcks[ack.key] ? C.greenBd : C.amberBd}`, borderRadius: 6, cursor: canAct ? 'pointer' : 'not-allowed' }}
                >
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: suspicionAcks[ack.key] ? C.green : '#fff', border: `2px solid ${suspicionAcks[ack.key] ? C.green : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    {suspicionAcks[ack.key] && <CheckSquare size={10} color="#fff" />}
                  </div>
                  <span style={{ fontSize: T.small, color: suspicionAcks[ack.key] ? C.green : '#78350f', fontWeight: suspicionAcks[ack.key] ? 600 : 400, lineHeight: 1.5 }}>{ack.label}</span>
                </div>
              ))}
            </div>
          )}
          {isFilingTransition && (
            <div style={{ marginBottom: 18, padding: '14px 16px', background: C.priBg || '#f0f7fa', border: `1px solid ${C.priBd || '#b8d8e8'}`, borderRadius: 10 }}>
              <div style={{ fontSize: T.small, fontWeight: 700, color: C.pri, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>AUSTRAC filing details</div>
              <div style={{ fontSize: T.small, color: C.slate, marginBottom: 12, lineHeight: 1.5 }}>
                Record the SMR lodgement details. Use <b>Manual</b> if the SMR was submitted through the AUSTRAC website directly; <b>Portal</b> if filed via an integrated channel; <b>System</b> is reserved for future in-app submission.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: T.small, fontWeight: 600, color: C.slateL, display: 'block', marginBottom: 4 }}>Filing method *</label>
                  <select value={filingMethod} onChange={(e) => setFilingMethod(e.target.value)} disabled={!canAct} style={{ width: '100%', padding: '8px 10px', fontSize: T.normal, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F.sans, background: canAct ? '#fff' : C.slateBg, color: C.ink, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="manual">Manual (AUSTRAC portal)</option>
                    <option value="portal">Portal (integrated)</option>
                    <option value="system">System</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: T.small, fontWeight: 600, color: C.slateL, display: 'block', marginBottom: 4 }}>Filed at *</label>
                  <input type="datetime-local" value={filedAtLocal} onChange={(e) => setFiledAtLocal(e.target.value)} disabled={!canAct} style={{ width: '100%', padding: '8px 10px', fontSize: T.normal, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F.sans, background: canAct ? '#fff' : C.slateBg, color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: T.small, fontWeight: 600, color: C.slateL, display: 'block', marginBottom: 4 }}>AUSTRAC reference *</label>
                <input type="text" value={austracReference} onChange={(e) => setAustracReference(e.target.value.slice(0, 64))} disabled={!canAct} placeholder="e.g. SMR-2026-00123" maxLength={64} style={{ width: '100%', padding: '8px 10px', fontSize: T.normal, border: `1px solid ${austracReference.trim() ? C.greenBd : C.border}`, borderRadius: 6, fontFamily: F.sans, background: canAct ? '#fff' : C.slateBg, color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: T.small, fontWeight: 600, color: C.slateL, display: 'block', marginBottom: 4 }}>Filing notes (optional)</label>
                <textarea value={filingNotes} onChange={(e) => setFilingNotes(e.target.value.slice(0, 4000))} disabled={!canAct} placeholder="Lodgement confirmation, channel details, etc." rows={2} style={{ width: '100%', padding: '8px 10px', fontSize: T.normal, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F.sans, background: canAct ? '#fff' : C.slateBg, color: C.ink, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
            </div>
          )}
          {isClosingAfterInvestigation && (
            <div style={{ marginBottom: 18, padding: '12px 14px', background: '#fffbeb', border: `1px solid ${C.amberBd}`, borderLeft: `4px solid ${C.amber}`, borderRadius: 8 }}>
              <div style={{ fontSize: T.small, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Close resolution (SVCP §5.2) *
              </div>
              <select
                value={closeResolution}
                onChange={(e) => setCloseResolution(e.target.value)}
                disabled={!canAct}
                style={{ width: '100%', padding: '8px 10px', fontSize: T.normal, border: `1px solid ${closeResolution ? C.greenBd : C.border}`, borderRadius: 6, fontFamily: F.sans, background: canAct ? '#fff' : C.slateBg, color: C.ink, outline: 'none', boxSizing: 'border-box' }}
              >
                <option value="">Select a resolution…</option>
                {CASE_RESOLUTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {CASE_RESOLUTION_OPTIONS.find(o => o.value === closeResolution) && (
                <div style={{ fontSize: T.small, color: '#78350f', marginTop: 6, lineHeight: 1.5 }}>
                  {CASE_RESOLUTION_OPTIONS.find(o => o.value === closeResolution).blurb}
                </div>
              )}
            </div>
          )}
          {(rule.requiresReason || isClosingAfterInvestigation) && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{rule.reasonLabel || 'Decision narrative'}</div>
              <textarea value={reason} onChange={e => setReason(e.target.value)} disabled={!canAct} placeholder="Minimum 20 characters required — this becomes a permanent audit record" style={{ width: '100%', minHeight: 80, fontSize: T.normal, border: `1px solid ${reason.length >= 20 ? C.greenBd : C.border}`, borderRadius: 8, padding: '10px 12px', fontFamily: F.sans, boxSizing: 'border-box', resize: 'vertical', color: C.ink, lineHeight: 1.6, outline: 'none', background: canAct ? '#fff' : C.slateBg }} />
              <div style={{ fontSize: T.small, color: reason.length >= 20 ? C.green : C.slateL, marginTop: 4 }}>{reason.length} chars {reason.length < 20 ? `— ${20 - reason.length} more needed` : '— minimum met ✓'}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: '11px 0', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: T.normal, fontWeight: 700, color: C.slate, cursor: 'pointer', fontFamily: F.sans }}>Cancel</button>
            <button onClick={() => {
              if (!canConfirm) return
              const payload = { reason, assignee, observers }
              if (isSuspicionTransition) {
                payload.suspicion_basis = {
                  facts_documented: !!suspicionAcks.facts_documented,
                  innocent_explanation_considered: !!suspicionAcks.innocent_explanation_considered,
                  innocent_explanation_ruled_out: !!suspicionAcks.innocent_explanation_ruled_out,
                  reasonable_grounds_objective_test: !!suspicionAcks.reasonable_grounds_objective_test,
                  reason,
                }
              }
              if (isFilingTransition) {
                // Convert the datetime-local value to a proper ISO string so the backend
                // can parse it regardless of the user's locale formatting.
                const filedAtIso = filedAtLocal ? new Date(filedAtLocal).toISOString() : new Date().toISOString()
                payload.filing = {
                  austrac_reference: austracReference.trim(),
                  filing_method: filingMethod,
                  filed_at: filedAtIso,
                  notes: filingNotes.trim() || undefined,
                }
              }
              if (isClosingAfterInvestigation) {
                payload.resolution_status = closeResolution
              }
              onConfirm(payload)
            }} disabled={!canConfirm} style={{ flex: 2, padding: '11px 0', background: canConfirm ? (isBackward ? C.orange : C.pri) : '#e2e8f0', border: 'none', borderRadius: 8, fontSize: T.normal, fontWeight: 800, color: canConfirm ? '#fff' : C.slateL, cursor: canConfirm ? 'pointer' : 'not-allowed', fontFamily: F.sans, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              {isBackward ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
              {canConfirm ? 'Confirm Transition' : !canAct ? 'Role not permitted' : 'Complete all conditions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── MAIN ─── */
export default function InvestigationPipeline() {
  const navigate = useNavigate()
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)
  const [pendingTransition, setPendingTransition] = useState(null)
  const [toast, setToast] = useState(null)
  const { members: fetchedTeamMembers } = useStaffMembers()
  const [teamMembers, setTeamMembers] = useState([])
  const [routing, setRouting] = useState(() => buildRoutingDefaults([]))
  const [showSettings, setShowSettings] = useState(false)
  const [showStageLabels, setShowStageLabels] = useState(false)
  const [compactCards, setCompactCards] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [stageLabels, setStageLabels] = useState({})
  const location = useLocation()
  const { user: authUser } = useAuth()
  // Map auth role to pipeline role: admin → manager (can use Return override), operator/viewer → co
  const currentRole = (authUser?.role === 'admin' || authUser?.role === 'manager' || authUser?.role === 'mlro') ? 'manager' : 'co'

  const stagesWithLabels = STAGES.map(s => ({ ...s, label: stageLabels[s.id] ?? s.label }))

  const filteredCases = searchQuery.trim()
    ? cases.filter(c => {
        const q = searchQuery.trim().toLowerCase()
        return (
          (c.id || '').toLowerCase().includes(q) ||
          (c.patron?.name || '').toLowerCase().includes(q) ||
          (c.patron?.id || '').toLowerCase().includes(q) ||
          (c.alert?.id || '').toLowerCase().includes(q) ||
          (c.alert?.typology || '').toLowerCase().includes(q)
        )
      })
    : cases
  // Derive current user from auth context instead of hardcoding
  const user = authUser ? {
    name: authUser.name || authUser.username || 'Unknown',
    role: currentRole,
    label: currentRole === 'manager' ? 'Compliance Manager' : 'Compliance Officer',
    initials: (authUser.name || authUser.username || '??').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase(),
    color: currentRole === 'manager' ? C.purple : C.pri,
  } : { name: 'Unknown', role: 'co', label: 'Compliance Officer', initials: '??', color: C.pri }
  const [smrFiledCount, setSmrFiledCount] = useState(0)
  const [smrFiledCases, setSmrFiledCases] = useState([])

  useEffect(() => {
    let cancelled = false
    let visTimer = null
    const loadPipelineCases = async () => {
      try {
        setLoading(true)
        setError(null)
        const qs = buildInvestigationWorkspaceCasesQuery()
        const res = await authFetch(getLocalApiUrl(`/api/cases?${qs}`))
        if (!res.ok) {
          const errText = await res.text().catch(() => '')
          let msg = `Cases API failed (${res.status})`
          try {
            const j = JSON.parse(errText)
            if (j.error) msg = j.error
          } catch (_) {
            if (errText) msg = errText.slice(0, 200)
          }
          throw new Error(msg)
        }
        const json = await res.json()
        if (cancelled) return
        if (!json.success) throw new Error(json.error || 'Failed to fetch')
        const raw = json.data || []
        const newestFirst = [...raw].sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        )
        const onePerMember = dedupeInvestigationCasesForPipeline(newestFirst)
        const data = onePerMember.map(mapApiCaseToPipeline)
        setCases(data)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load pipeline')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadPipelineCases()
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || cancelled) return
      clearTimeout(visTimer)
      visTimer = setTimeout(() => {
        if (!cancelled) loadPipelineCases()
      }, 400)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      clearTimeout(visTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [location.key])

  // SMR Filed from registry (cases with SMR submissions) — aligns with SMR Registry page
  useEffect(() => {
    let cancelled = false
    authFetch(getLocalApiUrl('/api/registry/forms?limit=500'))
      .then(r => r.json())
      .then(j => {
        if (!cancelled && j.success && Array.isArray(j.data)) {
          setSmrFiledCount(j.data.length)
          const mapped = j.data.map(row => ({
            id: row.case_number || row.case_id,
            _dbId: row.case_id,
            stage: 'filed',
            _apiStatus: 'RESOLVED',
            priority: (row.severity || 'medium').toLowerCase(),
            patron: {
              id:
                resolvePatronListSecondaryId({
                  member_id: row.member_id,
                  source_data_snapshot: row.source_data_snapshot,
                }) || (row.member_id ? String(row.member_id).trim() : '—'),
              name: resolvePatronListTitle({
                member_name: row.member_name,
                member_id: row.member_id,
                case_number: row.case_number || row.case_id,
                id: row.case_id,
                source_data_snapshot: row.source_data_snapshot,
              }),
              risk: null,
            },
            alert: { id: '—', rule: 'SMR Filed', typology: row.venue_name || '—' },
            assignee: { name: row.submitted_by || '—', role: 'co', initials: (row.submitted_by || '—').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase() || '—' },
            stageEnteredAt: row.last_submitted_at,
            timeline: [],
            _fromRegistry: true,
          }))
          setSmrFiledCases(mapped)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/configuration'))
        const json = await res.json()
        if (cancelled || !json.success) return
        const pipeline = (json.data || []).find(c => c.category === 'Pipeline')
        const setting = pipeline?.settings?.find(s => s.name === 'INVESTIGATION_STAGE_LABELS')
        if (setting?.value) {
          try {
            const labels = JSON.parse(setting.value)
            if (labels && typeof labels === 'object') setStageLabels(labels)
          } catch (_) {}
        }
      } catch (_) {}
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    setTeamMembers(fetchedTeamMembers)
  }, [fetchedTeamMembers])

  useEffect(() => {
    if (!fetchedTeamMembers.length) return
    setRouting((prev) => {
      const empty = buildRoutingDefaults([])
      if (JSON.stringify(prev) === JSON.stringify(empty)) {
        return buildRoutingDefaults(fetchedTeamMembers)
      }
      return remapRoutingAssignees(prev, fetchedTeamMembers)
    })
  }, [fetchedTeamMembers])

  // Fetch saved routing defaults from backend
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/cases/pipeline/routing'))
        const json = await res.json()
        if (cancelled) return
        if (json.success && json.data && typeof json.data === 'object') {
          // Resolve role-based defaults to actual team member IDs
          const resolved = {}
          for (const [key, rule] of Object.entries(json.data)) {
            const assignee = rule.defaultAssignee || (rule.defaultRole ? teamMembers.find(m => m.role === rule.defaultRole)?.id : null) || null
            const observers = rule.observers || (rule.observerRoles ? teamMembers.filter(m => rule.observerRoles.includes(m.role)).map(m => m.id) : [])
            resolved[key] = { defaultAssignee: assignee, observers }
          }
          setRouting(resolved)
        }
      } catch (_) { /* keep computed defaults */ }
    })()
    return () => { cancelled = true }
  }, [teamMembers])

  const saveStageLabels = async (labels) => {
    const res = await authFetch(getLocalApiUrl('/api/configuration/INVESTIGATION_STAGE_LABELS'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: JSON.stringify(labels) }),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to save')
    setStageLabels(labels)
  }

  const casesByStage = id => {
    const pipelineCases = filteredCases.filter(c => c.stage === id)
    if (id !== 'filed') return pipelineCases
    const pipelineIds = new Set(pipelineCases.map(c => c._dbId || c.id))
    const q = searchQuery.trim().toLowerCase()
    const filteredRegistry = (q
      ? smrFiledCases.filter(c =>
          (c.id || '').toLowerCase().includes(q) ||
          (c.patron?.name || '').toLowerCase().includes(q) ||
          (c.patron?.id || '').toLowerCase().includes(q)
        )
      : smrFiledCases
    ).filter(c => !pipelineIds.has(c._dbId) && !pipelineIds.has(c.id))
    return [...pipelineCases, ...filteredRegistry]
  }
  const totalOpen = filteredCases.filter(c => !['filed', 'closed'].includes(c.stage)).length
  const criticalCount = filteredCases.filter(c => c.priority === 'critical' && !['filed', 'closed'].includes(c.stage)).length

  const showToast = (msg, color = C.green) => {
    setToast({ msg, color })
    setTimeout(() => setToast(null), 3000)
  }

  const handleTransition = (rule) => {
    if (!selected) return
    const fresh = cases.find((x) => x.id === selected.id) || selected
    setPendingTransition({ caseData: fresh, rule })
  }

  const handleSelectCase = async (c) => {
    setSelected(c)
    if (!c || c._fromRegistry) return
    const caseId = c._dbId || c.id
    if (!caseId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}`))
      if (!res.ok) return
      const json = await res.json()
      if (!json?.success) return
      const detail = mapApiCaseToPipeline(json.data)
      setCases(prev => prev.map(item => (item.id === c.id ? { ...item, ...detail, timeline: detail.timeline, suspicionFormedAt: detail.suspicionFormedAt } : item)))
      setSelected(prev => (prev?.id === c.id ? { ...prev, ...detail } : prev))
    } catch (_) {
      // Timeline fetch is best-effort; keep existing card data if unavailable.
    }
  }

  const confirmTransition = async ({ reason, assignee, observers, filing, suspicion_basis, resolution_status }) => {
    const { caseData, rule } = pendingTransition
    const newStatus = STAGE_TO_STATUS[rule.to]
    if (!newStatus) {
      setPendingTransition(null)
      showToast('Transition not yet supported for this stage', C.amber)
      return
    }
    if (String(newStatus).toUpperCase() === String(caseData._apiStatus || '').toUpperCase()) {
      setPendingTransition(null)
      showToast('Case is already in this stage — refreshing from server.', C.amber)
      handleSelectCase(caseData)
      return
    }
    try {
      const body = { status: newStatus, reason: reason || `Moved to ${rule.to}`, user_id: user.name }
      if (filing && rule.to === 'filed') body.filing = filing
      if (suspicion_basis && rule.to === 'suspicion_formed') body.suspicion_basis = suspicion_basis
      if (resolution_status && rule.to === 'closed') body.resolution_status = resolution_status
      const res = await authFetch(getLocalApiUrl(`/api/cases/${caseData._dbId || caseData.id}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Update failed')
      const atIso = new Date().toISOString()
      const ev = { type: 'stage_change', at: atIso, actor: user.name, from: caseData.stage, to: rule.to, note: reason || `Moved to ${rule.to}` }
      const updated = cases.map(c => (c.id === caseData.id ? { ...c, stage: rule.to, _apiStatus: newStatus, stageEnteredAt: atIso, suspicionFormedAt: (rule.to === 'suspicion_formed' ? atIso : c.suspicionFormedAt), timeline: mergeTimelineEvents([...(c.timeline || []), ev]) } : c))
      setCases(updated)
      setSelected(prev => (prev?.id === caseData.id ? { ...prev, stage: rule.to, _apiStatus: newStatus, stageEnteredAt: atIso, suspicionFormedAt: (rule.to === 'suspicion_formed' ? atIso : prev.suspicionFormedAt), timeline: mergeTimelineEvents([...(prev.timeline || []), ev]) } : prev))
      setPendingTransition(null)
      showToast(`${caseData.id} → ${stagesWithLabels.find(s => s.id === rule.to)?.label || rule.to}`, C.green)
    } catch (e) {
      showToast(e.message || 'Failed to update status', C.red)
    }
  }

  const handleOpenCase = (c) => {
    // Always open in the unified investigation page (includes inline ECDD + SMR sections)
    navigate(`/compliance-safe/investigations/${encodeURIComponent(c.id)}`)
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, fontFamily: F.sans }}>
        <div style={{ textAlign: 'center', color: C.slate, fontSize: T.normal }}>Loading pipeline…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100%', padding: 24, fontFamily: F.sans }}>
        <div style={{ padding: 24, background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 10, color: C.red, fontSize: T.normal }}>{error}</div>
        <Link to="/compliance-safe/investigations" style={{ display: 'inline-block', marginTop: 16, fontSize: T.normal, color: C.pri, fontWeight: 600 }}>← Back to Investigation List</Link>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: F.sans, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 8rem)' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', background: toast.color, color: '#fff', padding: '10px 22px', borderRadius: 30, fontSize: T.normal, fontWeight: 700, zIndex: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          {toast.msg}
        </div>
      )}

      {/* Stats row — cards left to right (CaseDashboard style) */}
      <div className="mb-3 flex flex-wrap items-center gap-4">
          {[
            { label: 'Open Cases', value: totalOpen, icon: Inbox, color: C.pri },
            { label: 'Critical', value: criticalCount, icon: AlertTriangle, color: C.red },
            { label: 'Pending Evidence', value: casesByStage('pending_evidence').length, icon: Clock, color: C.amber },
            { label: 'SMR Active', value: casesByStage('suspicion_formed').length + casesByStage('smr_draft').length + casesByStage('under_review').length, icon: FileText, color: C.red },
            { label: 'SMR Filed', value: smrFiledCount, icon: CheckCircle2, color: C.green },
          ].map((s, i) => {
            const Icon = s.icon
            return (
              <div key={i} className="card flex items-center flex-1 min-w-[140px]">
                <div className="flex-shrink-0">
                  <Icon size={32} color={s.color} />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">{s.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
                </div>
              </div>
            )
          })}
      </div>

      {/* Search + view + routing — below cards */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by case ID, name, member ID, alert…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-psg-teal focus:border-transparent"
            style={{ fontFamily: F.sans }}
          />
        </div>
        <div className="flex items-center gap-4" style={{ marginLeft: 'auto' }}>
          <button onClick={() => setCompactCards(v => !v)} className="flex items-center gap-2 text-sm font-medium text-psg-teal hover:text-psg-teal/80 hover:underline cursor-pointer" style={{ fontFamily: F.sans }}>
            {compactCards ? 'Normal view' : 'Compact view'}
          </button>
          <button onClick={() => setShowSettings(true)} className="flex items-center gap-2 text-sm font-medium text-psg-teal hover:text-psg-teal/80 hover:underline cursor-pointer" style={{ fontFamily: F.sans }}>
            <Settings size={16} color={C.pri} />
            Routing
          </button>
          <button onClick={() => setShowStageLabels(true)} className="flex items-center gap-2 text-sm font-medium text-psg-teal hover:text-psg-teal/80 hover:underline cursor-pointer" style={{ fontFamily: F.sans }}>
            <Type size={16} color={C.pri} />
            Stage Labels
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0 rounded-lg border border-gray-200 bg-gray-100" style={{ minHeight: 400 }}>
        <div style={{ flex: 1, display: 'flex', overflowX: 'auto', overflowY: 'hidden' }}>
          {stagesWithLabels.map(stage => {
            const stageCases = casesByStage(stage.id)
            const SIcon = stage.icon
            return (
              <div key={stage.id} style={{ minWidth: 230, maxWidth: 250, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${C.border}`, background: '#fff' }}>
                <div style={{ padding: '11px 13px', borderBottom: `1px solid ${C.border}`, background: stage.bg, flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <SIcon size={12} color={stage.color} />
                    <span style={{ fontSize: T.normal, fontWeight: 800, color: stage.color }}>{stage.label}</span>
                    <div style={{ marginLeft: 'auto', width: 19, height: 19, borderRadius: '50%', background: stageCases.length > 0 ? stage.color : 'transparent', border: `1px solid ${stage.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: T.small, fontWeight: 900, color: stageCases.length > 0 ? '#fff' : stage.color }}>{stageCases.length}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: T.small, color: C.slateL }}>{stage.sub}</div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '9px 9px' }}>
                  {stageCases.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 8px', color: C.slateL, fontSize: T.small }}>No cases</div>
                  ) : (
                    stageCases.map(c => (
                      <CaseCard key={c._dbId || c.id} c={c} onSelect={handleSelectCase} isSelected={selected?.id === c.id} compact={compactCards} stages={stagesWithLabels} />
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {selected && (
          <CaseDrawer
            c={selected}
            currentRole={currentRole}
            onClose={() => setSelected(null)}
            onTransition={handleTransition}
            onOpenCase={handleOpenCase}
            onNavigate={(path) => { setSelected(null); navigate(path) }}
            stages={stagesWithLabels}
          />
        )}
      </div>

      {pendingTransition && (
        <TransitionModal
          caseData={pendingTransition.caseData}
          rule={pendingTransition.rule}
          currentRole={currentRole}
          routing={routing}
          onConfirm={confirmTransition}
          onCancel={() => setPendingTransition(null)}
          stages={stagesWithLabels}
          teamMembers={teamMembers}
        />
      )}

      {showSettings && (
        <SettingsPanel
          routing={routing}
          onUpdate={setRouting}
          onClose={() => setShowSettings(false)}
          stageLabels={stageLabels}
          teamMembers={teamMembers}
        />
      )}
      {showStageLabels && (
        <StageLabelsModal
          stageLabels={stageLabels}
          onSaveStageLabels={saveStageLabels}
          onClose={() => setShowStageLabels(false)}
        />
      )}
    </div>
  )
}
